import { GppGoodOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';
import { useStoreState } from 'pullstate';
import { CurrentScan } from '../../../stores/CurrentScan';
import { AppStore } from '../../../stores/AppStore';
import { getSubCollectionData } from '../../../utils/getData';
import { useNavigate } from 'react-router-dom';
import { LMSStore } from '../../../stores/LMSStore';

const ScanButton = ({ children }) => {
    const navigate = useNavigate();
    const location = window.location.pathname;
    const { userDoc } = useStoreState(AppStore);
    const {
        aiCheck,
        plagiarismCheck,
        factsCheck,
        readabilityCheck,
        grammarCheck,
        optimizerCheck,
        optimizerQuery,
        optimizerCountry,
        optimizerDevice,
        optimizerPublishingDomain,
        content,
        wordCount,
        credits,
        title,
        course,
        student,
        id,
        loading,
        model,
    } = useStoreState(CurrentScan);

    const scanNow = () => {
        const scanId = id || 'app-scan-' + title + '-' + Date.now();

        CurrentScan.update((s) => {
            s.scanDisabled = true;
            s.error = false;
            s.scanId = scanId;
            s.loading = true;
        });

        const scanTitle = title || content.substring(0, 50);

        const requestBody = {
            originalContent: content,
            creditCost: credits,
            title: scanTitle,
            user: userDoc.uid,
            course,
            student,
            id: scanId,
            aiModelVersion: model, // Keep this name for internal use, gets mapped to 'aiModel' in function

            // These match the Originality.ai API parameter names exactly
            aiCheck,
            plagiarismCheck,
            factsCheck,
            readabilityCheck,
            grammarCheck,
            optimizerCheck,
            storeScan: true,
        };

        // Add optimizer fields only if optimizer is enabled
        if (optimizerCheck) {
            requestBody.optimizerQuery = optimizerQuery;
            requestBody.optimizerCountry = optimizerCountry;
            requestBody.optimizerDevice = optimizerDevice;
            requestBody.optimizerPublishingDomain = optimizerPublishingDomain;
        }

        // console.log('Scan request body:', requestBody);

        fetch(`https://passedappscanv3-pikhiwvvfa-uc.a.run.app`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        })
            .then(async (response) => {
                if (response.status === 200) {
                    AppStore.update((s) => {
                        s.userDoc.credits = s.userDoc.credits - credits;
                    });
                    return response.json();
                } else {
                    const errorText = await response.text();
                    console.error('Scan API Error', response.status, errorText);
                    throw new Error('Non-200 response');
                }
            })
            .then((data) => {
                if (data?.result?.code === 'ERR_BAD_REQUEST') {
                    throw new Error('Bad request from scan API');
                }
                // console.log('Scan response data:', data);
                // Store the scan results in the CurrentScan store
                CurrentScan.update((s) => {
                    s.scanResults = data; // Store the full response
                    s.scanDisabled = false;
                    s.loading = false;
                });

                getSubCollectionData(userDoc.uid, 'scans', LMSStore);
                getSubCollectionData(userDoc.uid, 'students', LMSStore);
                getSubCollectionData(userDoc.uid, 'courses', LMSStore);

                navigate(location === '/ext-scan' ? `/ext-scans/${data.id}` : `/scans/${data.id}`);
            })
            .catch((err) => {
                console.error('Scan error', err);
                CurrentScan.update((s) => {
                    s.error = true;
                    s.scanDisabled = false;
                    s.loading = false;
                });
            });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            {children}
            <LoadingButton
                disabled={
                    wordCount < 50 ||
                    credits > userDoc.credits ||
                    (!aiCheck && !plagiarismCheck) ||
                    (wordCount > 2999 && plagiarismCheck)
                }
                size='large'
                loading={loading}
                loadingPosition='start'
                startIcon={<GppGoodOutlined />}
                variant='contained'
                color='secondary'
                onClick={scanNow}
            >
                {!loading ? 'Scan Now' : 'Scanning...'}
            </LoadingButton>
        </Box>
    );
};

export default ScanButton;
